'use client';

import { useEffect, useRef, useState } from 'react';
import {
  getConsent,
  saveConsent,
  createConsentRecord,
  logConsentAction,
  type ConsentRegion,
} from '@/lib/consent';

interface ConsentPreferenceCenterProps {
  region: ConsentRegion;
  onClose: () => void;
  onSave: () => void;
}

export default function ConsentPreferenceCenter({
  region,
  onClose,
  onSave,
}: ConsentPreferenceCenterProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const lastFocusableRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const [choices, setChoices] = useState({
    analytics: false,
    marketing: false,
    preferences: false,
  });

  // Load current consent on mount
  useEffect(() => {
    const existing = getConsent();
    if (existing) {
      setChoices(existing.choices);
    }
    // Store trigger element for focus return
    triggerRef.current = document.activeElement as HTMLElement;
  }, []);

  // Focus trap
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll(
          'button, [role="switch"]'
        );
        if (!focusableElements || focusableElements.length === 0) return;

        const first = focusableElements[0] as HTMLElement;
        const last = focusableElements[focusableElements.length - 1] as HTMLElement;

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleClose = () => {
    onClose();
    // Return focus to trigger
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const handleSavePreferences = () => {
    const record = createConsentRecord(region, choices);
    saveConsent(record);
    logConsentAction('save_preferences', region);
    onSave();
  };

  const handleAcceptAll = () => {
    const record = createConsentRecord(region, {
      analytics: true,
      marketing: true,
      preferences: true,
    });
    saveConsent(record);
    logConsentAction('accept_all', region);
    onSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="preferences-title"
        className="bg-surface border border-border rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <h2 id="preferences-title" className="font-headline text-lg font-semibold text-primary mb-6">
            Cookie Preferences
          </h2>

          <div className="space-y-4 mb-6">
            {/* Necessary - Always ON */}
            <div className="flex items-start justify-between p-4 bg-surface-hover rounded-sm border border-border">
              <div className="flex-1">
                <h3 className="font-headline text-sm font-semibold text-primary mb-1">
                  Necessary
                </h3>
                <p className="font-body text-xs text-text-secondary">
                  Essential for site functionality, security, and authentication. Always enabled.
                </p>
              </div>
              <button
                role="switch"
                aria-checked
                disabled
                className="ml-4 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-sm cursor-not-allowed"
              >
                ON
              </button>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between p-4 bg-surface-hover rounded-sm border border-border">
              <div className="flex-1">
                <h3 className="font-headline text-sm font-semibold text-primary mb-1">
                  Analytics
                </h3>
                <p className="font-body text-xs text-text-secondary">
                  Helps us understand how you use our site to improve your experience.
                </p>
              </div>
              <button
                ref={firstFocusableRef}
                role="switch"
                aria-checked={choices.analytics}
                onClick={() => setChoices({ ...choices, analytics: !choices.analytics })}
                className={`ml-4 px-3 py-1 text-xs font-semibold rounded-sm transition-colors duration-smooth ${
                  choices.analytics
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-border text-text-secondary'
                }`}
              >
                {choices.analytics ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between p-4 bg-surface-hover rounded-sm border border-border">
              <div className="flex-1">
                <h3 className="font-headline text-sm font-semibold text-primary mb-1">
                  Marketing
                </h3>
                <p className="font-body text-xs text-text-secondary">
                  Enables personalized ads and marketing campaigns based on your interests.
                </p>
              </div>
              <button
                role="switch"
                aria-checked={choices.marketing}
                onClick={() => setChoices({ ...choices, marketing: !choices.marketing })}
                className={`ml-4 px-3 py-1 text-xs font-semibold rounded-sm transition-colors duration-smooth ${
                  choices.marketing
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-border text-text-secondary'
                }`}
              >
                {choices.marketing ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Preferences */}
            <div className="flex items-start justify-between p-4 bg-surface-hover rounded-sm border border-border">
              <div className="flex-1">
                <h3 className="font-headline text-sm font-semibold text-primary mb-1">
                  Preferences
                </h3>
                <p className="font-body text-xs text-text-secondary">
                  Remembers your choices and settings for a personalized experience.
                </p>
              </div>
              <button
                role="switch"
                aria-checked={choices.preferences}
                onClick={() => setChoices({ ...choices, preferences: !choices.preferences })}
                className={`ml-4 px-3 py-1 text-xs font-semibold rounded-sm transition-colors duration-smooth ${
                  choices.preferences
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-border text-text-secondary'
                }`}
              >
                {choices.preferences ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleSavePreferences}
              className="flex-1 px-4 py-2 font-body text-sm font-semibold text-primary border border-border rounded-sm hover:bg-surface-hover transition-colors duration-smooth"
            >
              Save Preferences
            </button>
            <button
              ref={lastFocusableRef}
              onClick={handleAcceptAll}
              className="flex-1 px-4 py-2 font-body text-sm font-semibold text-primary-foreground bg-primary rounded-sm hover:bg-secondary transition-colors duration-smooth"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
